import { useProcessosJudiciaisContext } from "context/gestaoCobranca/ProcessosJudiciaisContext";
import React, { ReactNode, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import CustomButton from "shared/components/button/CustomButton";
import { DateRangePicker } from "shared/components/date/DateRangePicker";
import CustomModal from "shared/components/modal/CustomModal";
import { useGetRegistrosContaReceber } from "hooks/contaReceber/useGetRegistrosContaReceber";
import {
  formatarDataParaGetRequest,
  formatarValorMonetario,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { TipoBuscaUnidadeCliente } from "constants/EnumTipoBuscaUnidadeCliente";
import { SituacaoContaReceber } from "constants/EnumSituacaoContaReceber";
import { useGetValorTotalReceitas } from "hooks/contaReceber/useGetValorTotalReceitas";
import showAlert from "shared/util/functions/alert/showAlert";
import { mensagemAlteracoesNaoSalvas } from "shared/util/functions/alert/MensagensPadrõesAlerta";
import TabelaModalSelecionarCobrancasProcessoJudicial from "./TabelaModalSelecionarCobrancasProcessoJudicial";
import { RequestVincularCobrancasAoProcessoDTO } from "types/request/RequestVincularCobrancasAoProcessoDTO";
import { useLocation } from "react-router-dom";
import { usePostVincularCobrancasAoProcesso } from "hooks/processosJudiciais/usePostVincularCobrancasAoProcesso";

interface ModalSelecionarCobrancasProps {}

const ModalSelecionarCobrancas: React.FC<ModalSelecionarCobrancasProps> = (props) => {
  const [dataInicio, setDataInicio] = useState<Date | undefined>(undefined);
  const [dataFim, setDataFim] = useState<Date | undefined>(undefined);
  const location = useLocation();
  const { mutateAsync: vincularCobrancas } = usePostVincularCobrancasAoProcesso();

  const {
    showModalSelecionarCobrancas,
    openCloseModalSelecionarCobrancas,
    unidadeId,
    filtroTabelaSelecionarCobranca: filtro,
    setFiltroTabelaSelecionarCobranca,
    setRegistrosTabelaModalSelecionarCobrancas,
    registrosTabelaModalSelecionarCobrancas,
    cobrancasModalSelecionadas: cobrancasSelecionadas,
    idsCobrancasModalSelecionadas,
    setCobrancasModalSelecionadas: setCobrancasSelecionadas,
    setIdsCobrancasModalSelecionadas: setIdsCobrancasSelecionadas,
    opcaoCheckBoxSelecionarCobrancasModal,
    setOpcaoCheckBoxSelecionarCobrancasModal,
  } = useProcessosJudiciaisContext();

  const { data, isLoading } = useGetRegistrosContaReceber({
    page: filtro.paginaAtual - 1,
    size: 5,
    sort: filtro.sortString || "",
    unidadeId: unidadeId,
    dataVencimentoFim: formatarDataParaGetRequest(dataFim),
    dataVencimentoInicio: formatarDataParaGetRequest(dataInicio),
    unidadeCliente: TipoBuscaUnidadeCliente.UMA_UNIDADE,
    situacao: SituacaoContaReceber.ABERTA,
    naoExibirSubjudice: true,
    semVinculoComProcessoJudicial: true,
  });

  const { data: valorTotalReceitas, isFetching: isLoadingTotalReceitas } = useGetValorTotalReceitas({
    unidadeId: unidadeId,
    dataVencimentoFim: formatarDataParaGetRequest(dataFim),
    dataVencimentoInicio: formatarDataParaGetRequest(dataInicio),
    unidadeCliente: TipoBuscaUnidadeCliente.UMA_UNIDADE,
    situacao: SituacaoContaReceber.ABERTA,
    naoExibirSubjudice: true,
    semVinculoComProcessoJudicial: true,
  });

  useEffect(() => {
    setRegistrosTabelaModalSelecionarCobrancas(data);
  }, [data]);

  useEffect(() => {
    setFiltroTabelaSelecionarCobranca({ ...filtro, paginaAtual: 1 });
  }, [filtro.sortString, dataInicio, dataFim]);

  const renderBodyModalSelecionarCobrancas = (): ReactNode => {
    return (
      <Container>
        <Row className="mb-4">
          <Col sm={4} lg={4} md={4}>
            <DateRangePicker
              usarValidacaoParaFormulario={false}
              onChange={(datas) => {
                setDataInicio(datas[0]);
                setDataFim(datas[1]);
                setCobrancasSelecionadas([]);
                setIdsCobrancasSelecionadas([]);
              }}
              onClear={() => {
                setDataInicio(undefined);
                setDataFim(undefined);
                setCobrancasSelecionadas([]);
                setIdsCobrancasSelecionadas([]);
              }}
              id="periodo-vencimento-cobrancas"
              label="Período de vencimento"
            />
          </Col>
        </Row>
        <Row>
          <TabelaModalSelecionarCobrancasProcessoJudicial isLoading={isLoading} />
        </Row>
        {renderValoresReceitas()}
      </Container>
    );
  };

  const renderValoresReceitas = () => {
    if (!isLoading && !isLoadingTotalReceitas && registrosTabelaModalSelecionarCobrancas?.totalElements) {
      let valorTotalReceitasSelecionadas = 0;
      if (cobrancasSelecionadas.length > 0) {
        cobrancasSelecionadas.forEach((cobranca) => {
          valorTotalReceitasSelecionadas += cobranca.valor;
        });
      }

      return (
        registrosTabelaModalSelecionarCobrancas?.totalElements > 0 && (
          <Row className="mt-3">
            <span>Valor total das receitas : R$ {formatarValorMonetario(valorTotalReceitas ?? 0)}</span>
            <span>
              Valor total das receitas selecionadas: R${" "}
              {getValorReceitasSelecionadas(valorTotalReceitasSelecionadas ?? 0)}
            </span>
          </Row>
        )
      );
    }
  };

  const getValorReceitasSelecionadas = (valorReceitasSelecionadas: number) => {
    return opcaoCheckBoxSelecionarCobrancasModal === "TODAS_FILTRO"
      ? formatarValorMonetario(valorTotalReceitas ?? 0)
      : formatarValorMonetario(valorReceitasSelecionadas ?? 0);
  };

  const renderFooterSelecionarCobrancas = (): ReactNode => {
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-selecionar-cobrancas-processo-judicial"
            label="Cancelar"
            onClick={onCancel}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-selecionar-modal-selecionar-cobrancas-processo-judicial"
            label="Selecionar"
            onClick={selecionarCobrancas}
            variant="primary"
            className="ps-4 pe-4"
          />
        </Col>
      </Row>
    );
  };

  const selecionarCobrancas = async () => {
    if (
      data?.totalElements > 0 &&
      (cobrancasSelecionadas.length > 0 || opcaoCheckBoxSelecionarCobrancasModal === "TODAS_FILTRO")
    ) {
      const dto = {
        processoJudicialId: location.state?.processoJudicialId,
        idsContasReceber: idsCobrancasModalSelecionadas,
        isTodasDoFiltro: opcaoCheckBoxSelecionarCobrancasModal === "TODAS_FILTRO",
        filtroUtilizado: {
          unidadeId: unidadeId,
          dataVencimentoFim: formatarDataParaGetRequest(dataFim),
          dataVencimentoInicio: formatarDataParaGetRequest(dataInicio),
          unidadeCliente: TipoBuscaUnidadeCliente.UMA_UNIDADE,
          situacaoContaReceber: SituacaoContaReceber.ABERTA,
          naoExibirSubjudice: true,
          semVinculoComProcessoJudicial: true,
        },
      } as RequestVincularCobrancasAoProcessoDTO;
      await vincularCobrancas(dto);
    }

    closeModal();
  };

  const onCancel = () => {
    if (cobrancasSelecionadas.length > 0 || opcaoCheckBoxSelecionarCobrancasModal === "TODAS_FILTRO") {
      showAlert({
        texto: mensagemAlteracoesNaoSalvas(),
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => closeModal(),
      });
    } else {
      closeModal();
    }
  };

  const resetarFiltro = () => {
    setDataInicio(undefined);
    setDataFim(undefined);
    setFiltroTabelaSelecionarCobranca({
      ordenacaoAscOuDesc: undefined,
      ordenarPorColuna: "",
      paginaAtual: 1,
      sortString: "",
    });
  };

  const closeModal = () => {
    resetarFiltro();
    setCobrancasSelecionadas([]);
    setIdsCobrancasSelecionadas([]);
    setOpcaoCheckBoxSelecionarCobrancasModal("NENHUMA");
    openCloseModalSelecionarCobrancas();
  };
  return (
    <CustomModal
      titulo="Selecionar cobranças"
      onCloseModal={() => closeModal()}
      customFooter={renderFooterSelecionarCobrancas()}
      showModal={showModalSelecionarCobrancas}
      size="xl"
      tipoModal="MODAL_LATERAL"
      fecharModalAoClicarFora={
        cobrancasSelecionadas.length === 0 && !(opcaoCheckBoxSelecionarCobrancasModal === "TODAS_FILTRO")
      }
      scrollable
    >
      {renderBodyModalSelecionarCobrancas()}
    </CustomModal>
  );
};

export default ModalSelecionarCobrancas;
